import type { StudioReviewRead } from '@/hooks/dashboard-data-state'
import type {
  AppRevenueSummary,
  KpiData,
  OrderRecord,
  PeriodFilter,
  ProductRecord,
  SettlementInfo,
  TimeSeriesPoint,
} from '@/types/dashboard'

export interface AudienceKpi {
  total_fans: number
  new_7d: number
  return_rate: number
}

export interface TrafficSource {
  source: string
  percentage: number
}

export interface FanRecord {
  id: string
  name: string
  email: string
  source: string
  joined: string
}

export interface ImpactReviewData {
  kpis: KpiData[]
  timeSeries: TimeSeriesPoint[]
  appRevenue: AppRevenueSummary[]
  settlement: SettlementInfo | null
}

export interface AudienceReviewData {
  kpis: AudienceKpi | null
  sources: TrafficSource[]
  fans: FanRecord[]
}

export interface StudioReviewDataClient {
  readSources(): Promise<StudioReviewRead<ProductRecord[]>>
  readActivity(): Promise<StudioReviewRead<OrderRecord[]>>
  readImpact(period: PeriodFilter): Promise<StudioReviewRead<ImpactReviewData>>
  readAudience(): Promise<StudioReviewRead<AudienceReviewData>>
}

export type StudioReviewDataProducer = Partial<StudioReviewDataClient>

type StudioReviewLane = 'sources' | 'activity' | 'impact' | 'audience'

export async function readStudioReviewBoundary<T>(
  lane: StudioReviewLane,
  period?: PeriodFilter,
): Promise<StudioReviewRead<T>> {
  const query = lane === 'impact' && period
    ? `?period=${encodeURIComponent(period)}`
    : ''
  const response = await fetch(`/api/studio/review-read/${lane}${query}`, {
    headers: { Accept: 'application/json' },
    method: 'GET',
  })

  if (!response.ok) {
    throw new Error(`Studio review read failed (${response.status})`)
  }

  return await response.json() as StudioReviewRead<T>
}

/**
 * Create a review-data boundary for one consumer.
 *
 * A caller may inject a bounded producer for a focused test or future
 * composition root. Each absent lane reads the same-origin review boundary.
 */
export function createStudioReviewDataClient(
  producer: StudioReviewDataProducer = {},
): StudioReviewDataClient {
  const {
    readSources = () => readStudioReviewBoundary<ProductRecord[]>('sources'),
    readActivity = () => readStudioReviewBoundary<OrderRecord[]>('activity'),
    readImpact = (period) => readStudioReviewBoundary<ImpactReviewData>('impact', period),
    readAudience = () => readStudioReviewBoundary<AudienceReviewData>('audience'),
  } = producer

  return {
    readSources,
    readActivity,
    readImpact,
    readAudience,
  }
}
